import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  StudioCanvas,
  BeatStrip,
  BeatCard,
  BeatState,
  BeatVariant,
  GenerateButton,
  Button,
  Tag,
} from "../../../../../../design-system";
import { Author } from "../../../../../../types/Author";
import { SlideContent } from "./types";
import {
  WorkflowStep,
  ImageProvider,
  StoryType,
  SlideTypeDefinition,
} from "./SlideGenerator.types";

export type SlideGeneratorStudioViewProps = {
  // State
  slides: SlideContent[];
  promptInputs: string[];
  currentStep: WorkflowStep;
  progress: string;
  progressPercent: number;
  isGenerating: boolean;
  isSaving: boolean;
  isDownloading: boolean;
  aiGeneratingIndex: number | null;
  aiGeneratingAll: boolean;
  aiGeneratingAllProgress: string;
  renderedSlides: string[];
  imageProvider: ImageProvider;
  storyType: StoryType;
  storyTypeDefinitions: SlideTypeDefinition[];
  authors: Author[];
  selectedAuthorId: string;
  selectedAuthorName: string;
  selectedAuthorProfilePhotoURL: string;
  editableStepText: string;
  canSave: boolean;

  // Auto-pick (when storyType === "auto", AI picks at generate time)
  autoPickedStoryType?: StoryType | null;
  autoPickReason?: string | null;
  isPickingStoryFormat?: boolean;

  // Handlers
  onStoryTypeChange: (t: StoryType) => void;
  onProviderChange: (p: ImageProvider) => void;
  onAuthorChange: (authorId: string) => void;
  onGenerate: () => void;
  onGenerateAll: () => void;
  onTitleChange: (idx: number, title: string) => void;
  onSubtitleChange: (idx: number, subtitle: string) => void;
  onPromptChange: (idx: number, value: string) => void;
  onSelectOption: (slideIdx: number, optIdx: number) => void;
  onGenerateAI: (idx: number) => void;
  onSearchUnsplash: (idx: number) => void;
  onUpload: (idx: number, file: File | null) => void;
  onSave: () => void;
  onDownloadSlide: (idx: number) => void;
  onDownloadAll: () => void;
  onClear: () => void;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
`;

const AutoPickRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: 6px 10px;
  border-radius: var(--radius-md);
  background: var(--color-accent-subtle);
  border: 1px solid var(--color-accent);
  color: var(--color-accent-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  line-height: 1.35;
`;

const AutoPickFormat = styled.span`
  font-weight: var(--font-weight-semibold);
`;

const AutoPickReason = styled.span`
  color: var(--color-text-secondary);
`;

const AutoPickChange = styled.button`
  appearance: none;
  margin-left: auto;
  background: transparent;
  border: 1px solid var(--color-accent);
  color: var(--color-accent-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  border-radius: var(--radius-pill);
  padding: 2px 10px;
  cursor: pointer;

  &:hover {
    background: var(--color-surface);
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
`;

const ToolGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ToolLabel = styled.span`
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ToolSelect = styled.select`
  appearance: none;
  height: 36px;
  padding: 0 var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  cursor: pointer;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Spacer = styled.div`
  flex: 1;
  min-width: var(--space-3);
`;

const DetailPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
`;

const DetailHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
`;

const DetailTitle = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const TextField = styled.input`
  appearance: none;
  width: 100%;
  height: 36px;
  padding: 0 var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

const TextAreaField = styled.textarea`
  appearance: none;
  width: 100%;
  min-height: 64px;
  resize: vertical;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-base);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FileButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 var(--space-3);
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    background: var(--color-surface-subtle);
    border-color: var(--color-border-strong);
  }
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  flex-wrap: wrap;
`;

const FooterStats = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const FooterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const SlidePreview = styled.div<{ $primary: string }>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  background: #000;
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const SlideOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: var(--space-6);
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.78) 0%,
    rgba(0, 0, 0, 0.35) 45%,
    rgba(0, 0, 0, 0) 75%
  );
`;

const SlideTitleText = styled.div<{ $primary: string }>`
  font-family: var(--font-display);
  font-size: clamp(20px, 4vw, 36px);
  font-weight: var(--font-weight-bold);
  color: #fff;
  line-height: 1.15;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);

  span.accent {
    color: ${(p) => p.$primary};
  }
`;

const SlideSubtitleText = styled.div`
  margin-top: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: rgba(255, 255, 255, 0.88);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
`;

const SlideIndexBadge = styled.div`
  position: absolute;
  top: var(--space-3);
  left: var(--space-3);
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.06em;
`;

type Props = SlideGeneratorStudioViewProps & {
  primaryColor?: string;
};

const slideStateFor = (
  slide: SlideContent | undefined,
  generatingThis: boolean
): BeatState => {
  if (generatingThis) return "generating";
  if (!slide) return "empty";
  if (slide.options && slide.options.length > 0) return "ready";
  return "queued";
};

export const SlideGeneratorStudioView: React.FC<Props> = (props) => {
  const {
    slides,
    promptInputs,
    currentStep,
    progress,
    progressPercent,
    isGenerating,
    isSaving,
    isDownloading,
    aiGeneratingIndex,
    aiGeneratingAll,
    aiGeneratingAllProgress,
    renderedSlides,
    imageProvider,
    storyType,
    storyTypeDefinitions,
    authors,
    selectedAuthorId,
    editableStepText,
    canSave,
    autoPickedStoryType,
    autoPickReason,
    isPickingStoryFormat,
    onStoryTypeChange,
    onProviderChange,
    onAuthorChange,
    onGenerate,
    onGenerateAll,
    onTitleChange,
    onSubtitleChange,
    onPromptChange,
    onSelectOption,
    onGenerateAI,
    onSearchUnsplash,
    onUpload,
    onSave,
    onDownloadSlide,
    onDownloadAll,
    onClear,
    primaryColor,
  } = props;

  const [activeIdx, setActiveIdx] = useState<number>(0);
  const safeActiveIdx = Math.min(activeIdx, Math.max(0, slides.length - 1));
  const activeSlide = slides[safeActiveIdx];
  const activeRendered = renderedSlides[safeActiveIdx];
  const activeOption = activeSlide?.options?.[activeSlide.selectedIndex];
  const activePrompt = promptInputs[safeActiveIdx] || activeSlide?.imagePrompt || "";

  const hasContent = !!editableStepText?.trim();
  const isBusy = isGenerating || isSaving || isDownloading;
  const hasFormats = storyTypeDefinitions.length > 0;
  const canGenerate = hasContent && hasFormats;
  const isThisAiGenerating = aiGeneratingIndex === safeActiveIdx;

  const canvasState = useMemo(() => {
    if (currentStep === "generating" || isGenerating) return "generating";
    if (slides.length === 0) return "empty";
    if (activeSlide && (!activeOption || !activeOption.imageUrl)) return "empty";
    return "ready";
  }, [currentStep, isGenerating, slides.length, activeSlide, activeOption]);

  const progressLabel = aiGeneratingAll ? aiGeneratingAllProgress : progress;

  return (
    <Container>
      <Toolbar>
        <ToolGroup>
          <ToolLabel>Story format</ToolLabel>
          <ToolSelect
            value={String(storyType)}
            onChange={(e) => onStoryTypeChange(e.target.value as StoryType)}
            disabled={isBusy || !hasFormats}
            title={!hasFormats ? "Run 🔎 Find top formats first" : undefined}
          >
            {!hasFormats && (
              <option value="auto">— Run format research first —</option>
            )}
            {hasFormats && <option value="auto">✨ Auto-pick</option>}
            {storyTypeDefinitions.map((def) => (
              <option key={String(def.type)} value={String(def.type)}>
                {def.icon} {def.label}
              </option>
            ))}
          </ToolSelect>
        </ToolGroup>

        <ToolGroup>
          <ToolLabel>Image model</ToolLabel>
          <ToolSelect
            value={imageProvider}
            onChange={(e) => onProviderChange(e.target.value as ImageProvider)}
            disabled={isBusy}
          >
            <option value="gemini">Gemini</option>
            <option value="gpt">GPT Image</option>
          </ToolSelect>
        </ToolGroup>

        <ToolGroup>
          <ToolLabel>Author reference</ToolLabel>
          <ToolSelect
            value={selectedAuthorId}
            onChange={(e) => onAuthorChange(e.target.value)}
            disabled={isBusy || authors.length === 0}
          >
            <option value="">No author</option>
            {authors.map((author) => (
              <option key={author.id} value={author.id}>
                {author.fullName}
              </option>
            ))}
          </ToolSelect>
        </ToolGroup>

        <Spacer />

        <GenerateButton
          label={slides.length > 0 ? "Regenerate" : "Generate"}
          hint={
            !hasContent
              ? "Add a step script first"
              : !hasFormats
              ? "Run 🔎 Find top formats in the inspector first"
              : slides.length > 0
              ? "Re-runs the title pass"
              : "6 slides"
          }
          loading={isGenerating}
          disabled={!canGenerate || isBusy}
          onClick={onGenerate}
          fullWidth={false}
        />
      </Toolbar>

      {storyType === "auto" && (isPickingStoryFormat || autoPickedStoryType) && (
        <AutoPickRow>
          <span>✨</span>
          {isPickingStoryFormat ? (
            <span>Picking the best format for this lesson...</span>
          ) : (
            <>
              <span>Picked</span>
              <AutoPickFormat>
                {storyTypeDefinitions.find((d) => d.type === autoPickedStoryType)?.label ??
                  autoPickedStoryType}
              </AutoPickFormat>
              {autoPickReason && (
                <AutoPickReason>— {autoPickReason}</AutoPickReason>
              )}
              <AutoPickChange
                type="button"
                onClick={() =>
                  onStoryTypeChange(
                    (autoPickedStoryType as StoryType) || ("hard-truth" as StoryType)
                  )
                }
                disabled={isBusy}
              >
                Lock this format
              </AutoPickChange>
            </>
          )}
        </AutoPickRow>
      )}

      <StudioCanvas
        aspect="9:16"
        state={canvasState}
        progressLabel={progressLabel || undefined}
        progressPercent={
          currentStep === "generating" && progressPercent > 0 ? progressPercent : undefined
        }
        emptyTitle={slides.length === 0 ? "No slides yet" : "Pick a slide"}
        emptyHint={
          slides.length === 0
            ? "Hit Generate to draft slide titles, image prompts, and stock photos from your script."
            : "Choose a slide below to preview and edit."
        }
        maxHeight="56vh"
      >
        {activeSlide && activeRendered ? (
          <img src={activeRendered} alt={activeSlide.title} />
        ) : activeSlide && activeOption?.imageUrl ? (
          <SlidePreview $primary={primaryColor || "#FFD700"}>
            <SlideImage src={activeOption.imageUrl} alt={activeSlide.title} />
            <SlideIndexBadge>
              {String(safeActiveIdx + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </SlideIndexBadge>
            <SlideOverlay>
              <SlideTitleText $primary={primaryColor || "#FFD700"}>
                {activeSlide.title}
              </SlideTitleText>
              {activeSlide.subtitle && (
                <SlideSubtitleText>{activeSlide.subtitle}</SlideSubtitleText>
              )}
            </SlideOverlay>
          </SlidePreview>
        ) : null}
      </StudioCanvas>

      {activeSlide && (
        <DetailPanel>
          <DetailHead>
            <DetailTitle>Slide {String(safeActiveIdx + 1).padStart(2, "0")}</DetailTitle>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Tag variant="neutral" size="sm">
                {activeSlide.options?.length || 0} option
                {(activeSlide.options?.length || 0) === 1 ? "" : "s"}
              </Tag>
              {activeOption?.isAiGenerated && (
                <Tag variant="accent" size="sm">
                  AI
                </Tag>
              )}
              {activeOption?.isUploaded && (
                <Tag variant="info" size="sm">
                  Uploaded
                </Tag>
              )}
            </div>
          </DetailHead>

          <FieldRow>
            <FieldLabel>Title</FieldLabel>
            <TextField
              value={activeSlide.title}
              onChange={(e) => onTitleChange(safeActiveIdx, e.target.value)}
              placeholder="Slide title"
              disabled={isBusy}
            />
          </FieldRow>

          <FieldRow>
            <FieldLabel>Subtitle</FieldLabel>
            <TextField
              value={activeSlide.subtitle || ""}
              onChange={(e) => onSubtitleChange(safeActiveIdx, e.target.value)}
              placeholder="Optional subtitle"
              disabled={isBusy}
            />
          </FieldRow>

          <FieldRow>
            <FieldLabel>Image prompt</FieldLabel>
            <TextAreaField
              value={activePrompt}
              onChange={(e) => onPromptChange(safeActiveIdx, e.target.value)}
              placeholder="Describe the image you want"
              disabled={isBusy}
            />
          </FieldRow>

          <ActionRow>
            <Button
              variant="primary"
              size="sm"
              isLoading={isThisAiGenerating}
              disabled={isBusy || !activePrompt.trim()}
              onClick={() => onGenerateAI(safeActiveIdx)}
            >
              ✨ Regenerate with AI
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={isBusy || !activePrompt.trim()}
              onClick={() => onSearchUnsplash(safeActiveIdx)}
            >
              🔎 Search Unsplash
            </Button>
            <FileButton>
              📤 Upload image
              <HiddenFileInput
                type="file"
                accept="image/*"
                disabled={isBusy}
                onChange={(e) => onUpload(safeActiveIdx, e.target.files?.[0] || null)}
              />
            </FileButton>
          </ActionRow>
        </DetailPanel>
      )}

      <BeatStrip
        title="Slides"
        hint={
          slides.length === 0
            ? "Nothing generated yet"
            : `${slides.length} slide${slides.length === 1 ? "" : "s"}`
        }
      >
        {slides.length === 0
          ? null
          : slides.map((slide, index) => {
              const variants: BeatVariant[] = (slide.options || []).map((opt, i) => ({
                id: `${index}-${i}`,
                kind: "image",
                src: opt.thumbnailUrl || opt.imageUrl,
              }));
              const generatingThis =
                aiGeneratingIndex === index || (aiGeneratingAll && aiGeneratingIndex === index);
              return (
                <BeatCard
                  key={`slide-${index}`}
                  index={index}
                  caption={slide.title}
                  state={slideStateFor(slide, generatingThis)}
                  variants={variants}
                  activeVariantIndex={slide.selectedIndex}
                  active={index === safeActiveIdx}
                  aspect="9:16"
                  onSelect={() => setActiveIdx(index)}
                  onVariantChange={(idx) => onSelectOption(index, idx)}
                  onRegenerate={() => onGenerateAI(index)}
                />
              );
            })}
      </BeatStrip>

      <FooterBar>
        <FooterStats>
          {slides.length > 0 && (
            <>
              <Tag variant="neutral" size="sm">
                {slides.length} slide{slides.length === 1 ? "" : "s"}
              </Tag>
              {aiGeneratingAll && <Tag variant="accent" size="sm">{aiGeneratingAllProgress}</Tag>}
            </>
          )}
        </FooterStats>
        <FooterActions>
          {slides.length > 0 && (
            <>
              <Button
                variant="secondary"
                size="sm"
                disabled={isBusy}
                onClick={onClear}
              >
                Clear
              </Button>
              <Button
                variant="secondary"
                size="sm"
                isLoading={aiGeneratingAll}
                disabled={isBusy}
                onClick={onGenerateAll}
              >
                AI on all
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={isBusy}
                onClick={() => onDownloadSlide(safeActiveIdx)}
              >
                Download
              </Button>
              <Button
                variant="secondary"
                size="sm"
                isLoading={isDownloading}
                disabled={isBusy}
                onClick={onDownloadAll}
              >
                Download all
              </Button>
              <Button
                variant="primary"
                size="sm"
                isLoading={isSaving}
                disabled={!canSave || isBusy}
                onClick={onSave}
              >
                Save slides
              </Button>
            </>
          )}
        </FooterActions>
      </FooterBar>
    </Container>
  );
};

SlideGeneratorStudioView.displayName = "SlideGeneratorStudioView";

export default SlideGeneratorStudioView;
